import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiDownload, FiEye, FiUpload } from "react-icons/fi";
import { MdAttachFile, MdDeleteOutline } from "react-icons/md";
import { IoCloseOutline } from "react-icons/io5";
import useClickOutside from "../useClickOutside";
import {
  HR_ALLOWED_UPLOAD_EXTENSIONS,
  HR_UPLOAD_ACCEPT,
  getUploadValidationError,
} from "./hrRecordsHelpers";

const formatFileSize = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size)) {
    return null;
  }

  return `${(size / 1024).toFixed(2)} KB`;
};

const normaliseFiles = (files) => {
  if (!files) return [];
  if (Array.isArray(files)) return files;
  if (files.file_id || files.filename || files.name) return [files];

  return Object.values(files).flatMap((value) => normaliseFiles(value));
};

const getFileName = (file) =>
  file.filename ||
  file.file_name ||
  file.original_name ||
  file.name ||
  "Attachment";

const getFileMeta = (file) => {
  if (file.file_size_kb !== undefined && file.file_size_kb !== null) {
    return `${file.file_size_kb} KB`;
  }

  return formatFileSize(file.file_size || file.size);
};

function HrUploadControl({
  buttonText = "Upload File",
  popupTitle = "Attachments",
  newFiles = [],
  existingFiles = [],
  onFilesSelected,
  onRemoveNewFile,
  onRemoveExistingFile,
  onViewExistingFile,
  onDownloadExistingFile,
  onValidationError,
  emptyMessage = "No files added yet",
  multiple = true,
  disabled = false,
  accept = HR_UPLOAD_ACCEPT,
  allowedExtensions = HR_ALLOWED_UPLOAD_EXTENSIONS,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const chipRef = useRef(null);
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  useClickOutside([popupRef, chipRef, buttonRef], () => {
    setIsOpen(false);
  });

  const normalisedNewFiles = normaliseFiles(newFiles);
  const normalisedExistingFiles = normaliseFiles(existingFiles);
  const totalFiles = normalisedNewFiles.length + normalisedExistingFiles.length;

  useEffect(() => {
    if (!totalFiles && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen, totalFiles]);

  const items = useMemo(
    () => [
      ...normalisedExistingFiles.map((file, index) => ({
        key: `existing-${file.file_id || file.id || getFileName(file)}-${index}`,
        name: getFileName(file),
        meta: getFileMeta(file),
        onView:
          typeof onViewExistingFile === "function"
            ? () => onViewExistingFile(file)
            : null,
        onDownload:
          typeof onDownloadExistingFile === "function"
            ? () => onDownloadExistingFile(file)
            : null,
        onRemove:
          typeof onRemoveExistingFile === "function"
            ? () => {
                onRemoveExistingFile(file);
                if (totalFiles === 1) {
                  setIsOpen(false);
                }
              }
            : null,
      })),
      ...normalisedNewFiles.map((file, index) => ({
        key: `new-${file.name}-${file.size}-${index}`,
        name: getFileName(file),
        meta: getFileMeta(file),
        onRemove:
          typeof onRemoveNewFile === "function"
            ? () => {
                onRemoveNewFile(index);
                if (totalFiles === 1) {
                  setIsOpen(false);
                }
              }
            : null,
      })),
    ],
    [
      normalisedExistingFiles,
      normalisedNewFiles,
      onDownloadExistingFile,
      onRemoveExistingFile,
      onRemoveNewFile,
      onViewExistingFile,
      totalFiles,
    ],
  );

  const handleChange = (event) => {
    const files = Array.from(event.target.files || []);
    const validationError = getUploadValidationError(files, allowedExtensions);
    if (validationError) {
      if (typeof onValidationError === "function") {
        onValidationError(validationError);
      }
      event.target.value = null;
      return;
    }

    if (files.length && typeof onFilesSelected === "function") {
      onFilesSelected(files);
    }
    event.target.value = null;
  };

  return (
    <div className="hr-records-upload-control">
      <div className="hr-records-upload-controls">
        {typeof onFilesSelected === "function" && (
          <>
            <button
              type="button"
              className="challan-btn"
              ref={buttonRef}
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              <FiUpload className="upload-icon" /> {buttonText}
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple={multiple}
              accept={accept}
              className="hr-records-upload-hidden"
              onChange={handleChange}
            />
          </>
        )}

        {totalFiles > 0 && (
          <button
            type="button"
            className="hr-records-upload-chip"
            ref={chipRef}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <MdAttachFile className="file-icon" /> {totalFiles}
          </button>
        )}
      </div>

      {isOpen && (
        <div className="hr-records-upload-popup" ref={popupRef}>
          <div className="hr-records-upload-popup-header">
            <div className="hr-records-upload-popup-title">{popupTitle}</div>
            <button
              type="button"
              className="hr-records-upload-popup-close"
              onClick={() => setIsOpen(false)}
            >
              <IoCloseOutline className="close-icon" />
            </button>
          </div>

          <div className="hr-records-upload-popup-body">
            {items.length ? (
              items.map((item) => (
                <div className="hr-records-upload-file-item" key={item.key}>
                  <div>
                    <div className="hr-records-upload-file-name">{item.name}</div>
                    {item.meta && (
                      <div className="hr-records-upload-file-meta">{item.meta}</div>
                    )}
                  </div>
                  {(item.onView || item.onDownload || item.onRemove) && (
                    <div className="hr-records-upload-file-actions">
                      {item.onView && (
                        <button
                          type="button"
                          className="hr-records-upload-file-action hr-records-upload-file-view"
                          onClick={item.onView}
                        >
                          <FiEye className="action-icon" />
                        </button>
                      )}
                      {item.onDownload && (
                        <button
                          type="button"
                          className="hr-records-upload-file-action hr-records-upload-file-download"
                          onClick={item.onDownload}
                        >
                          <FiDownload className="action-icon" />
                        </button>
                      )}
                      {item.onRemove && (
                        <button
                          type="button"
                          className="hr-records-upload-file-action hr-records-upload-file-delete"
                          onClick={item.onRemove}
                        >
                          <MdDeleteOutline className="action-icon" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="hr-records-upload-empty">{emptyMessage}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HrUploadControl;
